import { useMemo, useState } from 'react';
import { AssignmentDrawer, type DrawerTarget } from '../components/AssignmentDrawer';
import { BarList } from '../components/chart/BarList';
import { StackedColumns, type StackedSeries } from '../components/chart/StackedColumns';
import { SERIES_SLOTS, colorFor } from '../components/chart/palette';
import { formatMonthKey, formatMonthKeyLong } from '../lib/format';
import type { AggregateBundle, AreaId, Filters, Meta } from '../types';

interface OverviewProps {
  stats: AggregateBundle;
  filters: Filters;
  meta: Meta | undefined;
  onFilterChange: (patch: Partial<Filters>) => void;
  /** Sätter filtret och byter till Uppdrag-fliken. */
  onShowAssignments: (patch: Partial<Filters>) => void;
  /** Byter till Tekniker-fliken med urvalet intakt. */
  onShowTechnologies: () => void;
}

/** Lika många serier som det finns validerade färger. Skulle områdena bli fler
 *  viks överskottet ihop i stället för att få en påhittad färg. */
const MAX_AREA_SERIES = SERIES_SLOTS;

export function OverviewView({
  stats,
  filters,
  meta,
  onFilterChange,
  onShowAssignments,
  onShowTechnologies,
}: OverviewProps) {
  // Målet lagras som objekt. Att bygga { tech } vid varje rendering skulle ge
  // panelen ett nytt objekt varje gång, och dess hämtningseffekt skulle löpa amok.
  const [drawerTarget, setDrawerTarget] = useState<DrawerTarget | null>(null);

  const { series, valuesByMonth, foldedAreas } = useMemo(() => {
    const ranked = stats.areas.filter((area) => area.count > 0);
    const named = ranked.slice(0, MAX_AREA_SERIES);
    const folded = ranked.slice(MAX_AREA_SERIES);

    const seriesList: StackedSeries[] = named.map((area, index) => ({
      key: area.id,
      label: area.name,
      color: colorFor(index),
    }));
    if (folded.length > 0) {
      seriesList.push({ key: '__other', label: `Övriga (${folded.length})`, color: colorFor(MAX_AREA_SERIES) });
    }

    const rows = stats.months.map((monthKey) => {
      const byArea = stats.areaByMonth[monthKey] ?? {};
      const row: Record<string, number> = {};
      for (const area of named) row[area.id] = byArea[area.id] ?? 0;
      if (folded.length > 0) {
        row.__other = folded.reduce((sum, area) => sum + (byArea[area.id] ?? 0), 0);
      }
      return row;
    });

    return { series: seriesList, valuesByMonth: rows, foldedAreas: folded };
  }, [stats]);

  const topTech = stats.technologies.slice(0, 15).map((tech) => ({
    name: tech.name,
    value: tech.count,
    secondary: { label: 'Senaste 30 dagarna', value: tech.currentCount },
  }));

  const selectedMonthKey =
    filters.year && filters.month ? `${filters.year}-${String(filters.month).padStart(2, '0')}` : undefined;

  const estimatedShare = stats.estimatedDateAssignments;
  const selectedAreaName = filters.area ? stats.areas.find((area) => area.id === filters.area)?.name : undefined;

  return (
    <>
      <div className="panel-grid panel-grid--split">
        <section className="panel" style={{ gridColumn: '1 / -1' }}>
          <div className="panel__header">
            <h2 className="panel__title">Uppdrag per månad</h2>
            <span className="panel__tools">Klicka på en månad för att filtrera</span>
          </div>
          <p className="panel__subtitle">
            Fördelat på område, efter publiceringsmånad.
          </p>

          {estimatedShare > 0 ? (
            <div className="notice" style={{ marginTop: 0, marginBottom: 16 }}>
              <span aria-hidden="true">⚠</span>
              <span>
                {estimatedShare} av uppdragen i urvalet saknar publiceringsdatum hos källan och räknas därför till den
                månad de upptäcktes – vid första insamlingen blir det samma månad för allihop. Slå på{' '}
                <strong>Endast daterade</strong> i filtret för en rättvisande månadsfördelning.
              </span>
            </div>
          ) : null}
          <StackedColumns
            categories={stats.months}
            categoryLabels={stats.months.map(formatMonthKey)}
            series={series}
            values={valuesByMonth}
            height={300}
            selectedCategory={selectedMonthKey}
            onSelectCategory={(monthKey) => {
              const [year, month] = monthKey.split('-').map(Number);
              if (selectedMonthKey === monthKey) onFilterChange({ year: undefined, month: undefined });
              else onFilterChange({ year, month });
            }}
          />
          <div className="legend">
            {series.map((s) => (
              <button
                key={s.key}
                type="button"
                className="legend__item"
                onClick={() => s.key !== '__other' && onFilterChange({ area: s.key as AreaId })}
              >
                <span className="legend__swatch" style={{ background: s.color }} aria-hidden="true" />
                {s.label}
              </button>
            ))}
          </div>
          {foldedAreas.length > 0 ? (
            <p className="panel__tools" style={{ marginTop: 8 }}>
              Övriga: {foldedAreas.map((area) => area.name).join(', ')}
            </p>
          ) : null}
        </section>

        <section className="panel">
          <div className="panel__header">
            <h2 className="panel__title">
              {selectedAreaName ? `Mest efterfrågade tekniker inom ${selectedAreaName}` : 'Mest efterfrågade tekniker'}
            </h2>
            {stats.technologies.length > topTech.length ? (
              <button type="button" className="link-button" onClick={onShowTechnologies}>
                Visa alla {stats.technologies.length}
              </button>
            ) : null}
          </div>
          <p className="panel__subtitle">Antal uppdrag där tekniken nämns. Klicka för att se annonserna.</p>
          <BarList data={topTech} onSelect={(name) => setDrawerTarget({ tech: name })} selected={filters.tech} />
        </section>

        <section className="panel">
          <div className="panel__header">
            <h2 className="panel__title">Områden</h2>
          </div>
          <p className="panel__subtitle">
            Siffran är uppdragets huvudområde. Den gråa visar hur många som dessutom nämner området – det är de du
            får när du klickar.
          </p>
          <BarList
            data={stats.areas
              .filter((a) => a.count > 0)
              .map((area) => ({
                name: area.name,
                value: area.count,
                extra: { label: 'Nämner området', value: area.mentioned },
              }))}
            onSelect={(name) => {
              const area = stats.areas.find((a) => a.name === name);
              if (area) onFilterChange({ area: filters.area === area.id ? undefined : area.id });
            }}
            selected={stats.areas.filter((a) => a.id === filters.area).map((a) => a.name)}
          />
        </section>

        <section className="panel">
          <div className="panel__header">
            <h2 className="panel__title">Källor</h2>
          </div>
          <p className="panel__subtitle">Var uppdragen kommer ifrån.</p>
          <BarList
            data={stats.sources.filter((s) => s.count > 0).map((source) => ({ name: source.name, value: source.count }))}
            onSelect={(name) => {
              const source = stats.sources.find((s) => s.name === name);
              if (source) onFilterChange({ source: filters.source === source.id ? undefined : source.id });
            }}
            selected={stats.sources.filter((s) => s.id === filters.source).map((s) => s.name)}
          />
        </section>

        <section className="panel">
          <div className="panel__header">
            <h2 className="panel__title">Vanligaste orterna</h2>
          </div>
          <p className="panel__subtitle">Ort som angetts i annonsen. Klicka för att filtrera.</p>
          <BarList
            data={stats.topLocations.slice(0, 12).map((item) => ({ name: item.name, value: item.count }))}
            onSelect={(name) => onFilterChange({ location: filters.location === name ? undefined : name })}
            selected={filters.location ? [filters.location] : []}
            emptyText="Inga orter angivna i urvalet."
          />
        </section>

        <section className="panel">
          <div className="panel__header">
            <h2 className="panel__title">Återkommande kunder</h2>
          </div>
          <p className="panel__subtitle">Slutkund eller förmedlande bolag, där det framgår.</p>
          <BarList data={stats.topClients.slice(0, 12).map((item) => ({ name: item.name, value: item.count }))} emptyText="Ingen kund angiven i urvalet." />
        </section>
      </div>

      {stats.months.length > 0 ? (
        <p className="panel__tools" style={{ marginTop: 18 }}>
          Perioden {formatMonthKeyLong(stats.months[0])} – {formatMonthKeyLong(stats.months[stats.months.length - 1])}.
        </p>
      ) : null}

      <AssignmentDrawer
        target={drawerTarget}
        filters={filters}
        meta={meta}
        onClose={() => setDrawerTarget(null)}
        onShowAll={onShowAssignments}
      />
    </>
  );
}
